import * as React from 'react';
import { useState, useEffect } from 'react';
import * as API from '../Utils/API.js';
import { htmlIf } from '../Utils/HTML';
import { User } from '../Types/User';
import LeadMagnetGrid from './LeadMagnetGrid';
import { LeadMagnet } from '../Types/LeadMagnet';
import { DismissibleModal } from '../Components/Modal';
import { trackEvent } from '../Utils/Tracking'

// These props are passed in via the LeadMagnetService#lead_magnet_props function in Ruby.
type Props =
  { leadMagnet: LeadMagnet
  , user?: User
  , isRegistered: boolean
  , isDeclined: boolean
  , utmLcSource?: string
  , otherLeadMagnets: LeadMagnet[]
  , showVegasVersion: boolean
  }

export const LandingPage = (props: Props) => {
  const [isRegistered, setIsRegistered] = useState(props.isRegistered);

  const VegasVersion = () => (
    <>
      <div className="section bg-accent py-5 py-md-6">
        <div className="container">
          <div className="row justify-content-center text-center">
            {/* Image (Centered Above on Mobile, Side-by-Side on Desktop) */}
            <div className="col-lg-5 mb-4 mb-lg-0 order-lg-2">
              <div className="rounded mx-auto shadow-lg"
                style={{
                  width: '70%', // Smaller image size
                  maxWidth: '200px', // Prevents it from being too large
                  aspectRatio: '8.5 / 11' // Maintains aspect ratio
                }}
              >
                <img src={props.leadMagnet.coverPhotoUrl} className="rounded-1 w-100 h-100" style={{objectFit: 'cover'}}/>
              </div>
            </div>

            {/* Text & Button */}
            <div className="col-lg-6 text-center text-lg-start">
              <h2 className="text-dark fw-bold">{props.leadMagnet.title}</h2>
              <div className="fs-xl text-gray-900 mt-2">
                Download our FREE eBook to turn likes into loyal customers and get a change to win a $500 Lucia Gift Card.
              </div>

              <div className="mt-4">
                { isRegistered
                ? <>
                    <div className="badge bg-success text-white py-2 px-3 fs-lg">✅ You got it!</div>
                    <div className="mt-2 text-secondary fs-sm">
                      <a href={props.leadMagnet.downloadUrl} className="text-decoration-none">Download another copy</a>
                    </div>
                  </>
                : <LeadMagnetForm
                    leadMagnet={props.leadMagnet}
                    user={props.user}
                    utmLcSource={props.utmLcSource}
                    showPhoneField={true}
                    onRegister={() => setIsRegistered(true)}
                  />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="section pt-15 pb-5">
        <div className="container">
          <h2 className="text-center">What’s Inside the eBook?</h2>
          <div className="mt-2 text-gray-900 text-center">This eBook is packed with actionable insights to help you grow your business!</div>
          <div className="row mt-8">
            <div className="col-lg-4">
              <div className="avatar avatar-md">
                <div className="avatar-title bg-accent rounded-circle text-primary fs-xxl">
                  <i className="ai-chart" />
                </div>
              </div>
              <div className="mt-2 fw-semibold text-dark">Proven Engagement Hacks</div>
              <div className="mt-2 text-dark">
                Learn how to create content that captivates your audience and keeps them coming back for more.
              </div>
            </div>
            <div className="col-lg-4 mt-4 mt-lg-0">
              <div className="avatar avatar-md">
                <div className="avatar-title bg-accent rounded-circle text-primary fs-xxl">
                  <i className="ai-filter" />
                </div>
              </div>
              <div className="mt-2 fw-semibold text-dark">Step-by-Step Booking Funnel</div>
              <div className="mt-2 text-dark">
                Build a seamless journey from Instagram engagement to confirmed bookings.
              </div>
            </div>
            <div className="col-lg-4 mt-4 mt-lg-0">
              <div className="avatar avatar-md">
                <div className="avatar-title bg-accent rounded-circle text-primary fs-xxl">
                  <i className="ai-circle-check" />
                </div>
              </div>
              <div className="mt-2 fw-semibold text-dark">Expert Tips for Instagram Growth</div>
              <div className="mt-2 text-dark">
                Get advice from social media pros who’ve been there and done it.
              </div>
            </div>
          </div>
          <div className="text-center">
            <button className="btn btn-primary mt-4"
              onClick={() => window.scrollTo({ top: 0, behavior: "auto" })}
            >
              Download now
            </button>
          </div>
        </div>
      </div>
    </>
  )

  const NormalVersion = () => (
    <>
      <div className="section bg-accent py-5 py-md-6">
        <div className="container">
          <div className="row justify-content-center text-center">
            {/* Image (Centered Above on Mobile, Side-by-Side on Desktop) */}
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="rounded mx-auto shadow-lg"
                style={{
                  width: '70%', // Smaller image size
                  maxWidth: '300px', // Prevents it from being too large
                  aspectRatio: '8.5 / 11' // Maintains aspect ratio
                }}
              >
                <img src={props.leadMagnet.coverPhotoUrl} className="rounded-1 w-100 h-100" style={{objectFit: 'cover'}}/>
              </div>
            </div>

            {/* Text & Button */}
            <div className="col-lg-6 text-center text-lg-start">
              <h2 className="text-dark fw-bold">{props.leadMagnet.title}</h2>
              <p className="text-muted mt-2">
                Get instant access to this free resource.
              </p>

              <div className="mt-4">
                { isRegistered
                ? <>
                    <div className="badge bg-success text-white py-2 px-3 fs-lg">✅ You got it!</div>
                    <div className="mt-2 text-secondary fs-sm">
                      <a href={props.leadMagnet.downloadUrl} className="text-decoration-none">Download another copy</a>
                    </div>
                  </>
                : <LeadMagnetForm
                    leadMagnet={props.leadMagnet}
                    user={props.user}
                    utmLcSource={props.utmLcSource}
                    showPhoneField={true}
                    onRegister={() => setIsRegistered(true)}
                  />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="section pb-8 mb-n8 bg-white">
        <div className="container pt-5">
          <h4 className="">Other Great Downloads</h4>
          <LeadMagnetGrid
            leadMagnets={props.otherLeadMagnets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())}
            targetBlank={true}
          />
        </div>
      </div> */}
    </>
  )

  return (
    <>
      { props.showVegasVersion
      ? VegasVersion()
      : NormalVersion()
      }
    </>
  )
}

export const Modal = (props: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRegistered, setIsRegistered] = useState(props.isRegistered);

  useEffect(() => {
      if (!props.isRegistered && !props.isDeclined) {
        // If we're planning to display the modal, show it after 1.5 seconds on the page and track an event.
        setTimeout(() => {
          setIsVisible(true);
          trackEvent('Lead Magnet: Modal Viewed', {
            lead_magnet_id: props.leadMagnet.id,
            lead_magnet_title: props.leadMagnet.title,
            utm_lc_source: props.utmLcSource
          })
        }, 1500);
      } else { }
    }, [])

  function declineLeadMagnet() {
    setIsVisible(false);
    const body = { leadMagnetId: props.leadMagnet.id }
    API.post("api_free_resources_decline_path", body).then(function (result) {
      if (result['error']) {
      } else {
        setIsVisible(false);
        trackEvent('Lead Magnet: Declined', {
          lead_magnet_id: props.leadMagnet.id,
          lead_magnet_title: props.leadMagnet.title,
          utm_lc_source: props.utmLcSource
        })
      }
    })
  }

  return (
    htmlIf(isVisible,
      <DismissibleModal
        dialogClass='modal-lg'
        contentClass='overflow-hidden'
        bodyClass='bg-accent'
        body={
          <div>
            <div className="">
              <div className="row g-0 no-gutters align-items-center">
                {/* Image (Centered Above on Mobile, Side-by-Side on Desktop) */}
                <div className="col-lg-6 mb-4 mb-lg-0 bg-accent overflow-hidden">
                  <div className="rounded mx-auto shadow-lg"
                    style={{
                      width: '70%', // Smaller image size
                      maxWidth: '300px', // Prevents it from being too large
                      aspectRatio: '8.5 / 11' // Maintains aspect ratio
                    }}
                  >
                    <img src={props.leadMagnet.coverPhotoUrl} className="rounded-1 w-100 h-100" style={{objectFit: 'cover'}}/>
                  </div>
                </div>

                {/* Text & Button */}
                <div className="col-lg-6 text-center text-lg-start">
                  <h3 className="text-dark fw-bold">{props.leadMagnet.popupHeading}</h3>
                  <div className="fs-md text-dark mt-1">{props.leadMagnet.popupDescription}</div>
                  <div className="mt-4">
                    { isRegistered
                    ? <>
                        <div className="badge bg-success text-white py-2 px-3 fs-lg">✅ You got it!</div>
                        <div className="mt-2 text-secondary fs-sm">
                          <a href={props.leadMagnet.downloadUrl} className="text-decoration-none">Download another copy</a>
                        </div>
                      </>
                    : <div className="">
                        <LeadMagnetForm
                          leadMagnet={props.leadMagnet}
                          user={props.user}
                          utmLcSource={props.utmLcSource}
                          showPhoneField={false}
                          onRegister={() => setIsRegistered(true)}
                        />
                        <div className="mt-1">
                          <button className="btn btn-link p-0 fs-sm text-secondary" onClick={declineLeadMagnet}>
                            No thanks
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        onDismiss={() => setIsVisible(false)}
      />
    )
  )
}

type LeadMagnetFormProps =
  { leadMagnet: LeadMagnet
  , user?: User
  , utmLcSource?: string
  , showPhoneField: boolean
  , onRegister: () => void;
  }


const LeadMagnetForm = (props: LeadMagnetFormProps) => {
  const [anonFirstName, setAnonFirstName] = useState('');
  const [anonLastName, setAnonLastName] = useState('');
  const [anonEmail, setAnonEmail] = useState('');
  const [anonPhone, setAnonPhone] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  function trackRegistration() {
    // We also have a Lead Magnet: Registration event on the server side, but we want to log this as well.
    trackEvent('Lead Magnet: Registered (Client-Side)', {
      lead_magnet_id: props.leadMagnet.id,
      lead_magnet_title: props.leadMagnet.title,
      utm_lc_source: props.utmLcSource
    })
  }

  function handleRegisterClicked() {
    const body = { leadMagnetId: props.leadMagnet.id, utmLcSource: props.utmLcSource }
    API.post("api_free_resources_register_path", body).then(function (result) {
      if (result['error']) {
      } else {
        props.onRegister();
        trackRegistration();
        window.location.href = props.leadMagnet.downloadUrl;
      }
    })
  }

  function handleAnonRegisterClicked() {
    if (anonFirstName.length > 0 && anonLastName.length > 0 && anonEmail.length > 5) {
      const body = {
        leadMagnetId: props.leadMagnet.id,
        firstName: anonFirstName,
        lastName: anonLastName,
        email: anonEmail,
        phoneNumber: anonPhone,
        utmLcSource: props.utmLcSource
      }
      API.post("api_free_resources_register_path", body).then(function (result) {
        if (result['error']) {
          setErrorMessage(result['error']);
          setShowErrors(true);
        } else {
          props.onRegister();
          trackRegistration();
          window.location.href = props.leadMagnet.downloadUrl;
        }
      })
    } else {
      setShowErrors(true);
    }
  }

  if (props.user) {
    return (
      <div>
        <button className="btn btn-primary t--download" onClick={handleRegisterClicked}>
          Download now
        </button>
      </div>
    )
  } else {
    return (
      <>
        <div className="row mt-3 text-start">
          <div className="col-sm-6">
            <label className="form-label">First name</label>
            <input
              className="form-control form-control-sm bg-white t--first-name" type="text"
              autoComplete='given-name'
              value={anonFirstName}
              onChange={(e) => setAnonFirstName(e.target.value)}
              placeholder='First name…'
            />
            {htmlIf(showErrors && !anonFirstName.length,
              <div className="fs-sm mt-1 text-danger fw-bold">Please enter your first name.</div>
            )}
          </div>
          <div className="col-sm-6 mt-2 mt-sm-0">
            <label className="form-label">Last name</label>
            <input
              className="form-control form-control-sm bg-white t--last-name" type="text"
              autoComplete='family-name'
              value={anonLastName}
              onChange={(e) => setAnonLastName(e.target.value)}
              placeholder='Last name…'
            />
            {htmlIf(showErrors && !anonLastName.length,
              <div className="fs-sm mt-1 text-danger fw-bold">Please enter your last name.</div>
            )}
          </div>
        </div>
        <div className="mt-2 text-start">
          <label className="form-label">Email</label>
          <input
            className="form-control form-control-sm bg-white t--email"
            type="email"
            autoComplete='email'
            value={anonEmail}
            onChange={(e) => setAnonEmail(e.target.value)}
            placeholder='Email…'
          />
          {htmlIf(showErrors && anonEmail.length < 6,
            <div className="fs-sm mt-1 text-danger fw-bold">Please enter a valid email.</div>
          )}
        </div>
        {htmlIf(props.showPhoneField,
          <div className="mt-2 text-start">
            <label className="form-label">Phone number</label>
            <input
              className="form-control form-control-sm bg-white t--phone"
              type="tel"
              autoComplete='tel'
              value={anonPhone}
              onChange={(e) => setAnonPhone(e.target.value)}
              placeholder="Enter phone number…"
            />
            <div className="fs-xs text-secondary mt-1">
              By providing your phone number, you agree to receive SMS messages from Lucia.
            </div>
          </div>
        )}
        <button className="btn btn-primary mt-3 t--download t--download-anonymous" onClick={handleAnonRegisterClicked}>
          Download now
        </button>
        {htmlIf(showErrors && errorMessage,
          <div className="fs-sm mt-1 text-danger fw-bold">{errorMessage}</div>
        )}
      </>
    )
  }
}
